/* eslint-disable react-hooks/exhaustive-deps */
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import InstituteOfChargeDetail from "pages/InstituteOfCharge/InstituteOfChargeDetail";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  editHigherAdminFeedbackRequest2,
  fetchHigherAdminRequestById,
} from "services/appealService";
import {
  AppealRequest,
  AppealRequestStatus,
  AppealRequestType,
  FILE_URL,
  HigherAdminFeedbackRequest,
  HigherAdminFeedbackRequestStatus,
  Roles,
} from "types";
import { DateView, errorHandlerOnForm, hasRole } from "utils/helper";
import HigherAdminRespondbackFeedback from "./fragment/HigherAdminRespondbackFeedback";
import EditHigherAdminFeedback from "./modal/EdittHigherAdminFeedback";
import HigherAdminAssignProsecutor from "./modal/HigherAdminAssignProsecutor";
import HigherAdminRespondBackComment from "./modal/HigherAdminRespondBackComment";
import RegistrarHigherAdminFeedback from "./modal/RegistrarHigherAdminFeedback";
import RegistrarSubmittedToHigherAdmin from "./modal/RegistrarSubmittedToHigherAdmin";
import DocumentViewerModal from "components/DocumentViewer";

// Enhanced status chip with better styling
export function getStatusChipColor(status: string) {
  const baseClasses =
    "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border";

  switch (status) {
    case AppealRequestStatus.PENDING:
      return `${baseClasses} bg-amber-50 text-amber-800 border-amber-200`;
    case AppealRequestStatus.FORWARDED_TO_REGISTRAR:
      return `${baseClasses} bg-blue-50 text-blue-800 border-blue-200`;
    case AppealRequestStatus.FORWARDED_TO_COORDINATOR:
      return `${baseClasses} bg-purple-50 text-purple-800 border-purple-200`;
    case AppealRequestStatus.SUBMITTED_TO_COURT:
      return `${baseClasses} bg-emerald-50 text-emerald-800 border-emerald-200`;
    case AppealRequestStatus.COORDINATOR_RESPONDED_BACK:
      return `${baseClasses} bg-pink-50 text-pink-800 border-pink-200`;
    case AppealRequestStatus.ASSIGNED_TO_PROSECUTOR:
      return `${baseClasses} bg-indigo-50 text-indigo-800 border-indigo-200`;
    case AppealRequestStatus.FORWARDED_TO_HIGHER_REGISTRAR:
      return `${baseClasses} bg-blue-100 text-blue-900 border-blue-300`;
    case AppealRequestStatus.FORWARDED_TO_HIGHER_CLERK:
      return `${baseClasses} bg-gray-100 text-gray-900 border-gray-300`;
    case AppealRequestStatus.FORWARDED_TO_HIGHER_COORDINATOR:
      return `${baseClasses} bg-purple-100 text-purple-900 border-purple-300`;
    case AppealRequestStatus.RESPONDED_BACK_TO_PROSECUTOR:
      return `${baseClasses} bg-orange-50 text-orange-800 border-orange-200`;
    case AppealRequestStatus.COMPLETED:
      return `${baseClasses} bg-green-100 text-green-900 border-green-300`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-800 border-gray-300`;
  }
}

// Reusable Components
const InfoCard: React.FC<{
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badgeCount?: number;
}> = ({ title, children, defaultOpen = false, badgeCount }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {badgeCount !== undefined && (
            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">
              {badgeCount}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && <div className="px-6 pb-4">{children}</div>}
    </div>
  );
};

const InfoRow: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className = "" }) => (
  <div
    className={`flex justify-between items-start py-3 border-b border-gray-100 last:border-b-0 ${className}`}
  >
    <dt className="text-sm font-medium text-gray-600 flex-shrink-0 pr-4">
      {label}
    </dt>
    <dd className="text-sm text-gray-900 text-right font-medium">{value}</dd>
  </div>
);

const FileAttachment: React.FC<{ name: string; url: string }> = ({
  name,
  url,
}) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
        <svg
          className="w-5 h-5 text-red-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <span className="font-medium text-gray-900">{name}</span>
    </div>
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="px-3 py-1 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
    >
      View
    </a>
  </div>
);

const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
}> = ({ onClick, children, variant = "outline", className = "" }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const ActionDropdownItem: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
}> = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors flex items-center gap-3"
  >
    {children}
  </button>
);

const HigherAdminDetail: React.FC = () => {
  const { id } = useParams();
  const { loginData } = useAuth();
  const navigate = useNavigate();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const [loading, setLoading] = React.useState(false);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [openRespondBack, setOpenRespondBack] = useState(false);
  const [openAssignProsecutor, setOpenAssignProsecutor] = useState(false);
  const [openSubmitToHigherAdmin, setOpenSubmitToHigherAdmin] = useState(false);
  const [openHigherAdminFeedback, setOpenHigherAdminFeedback] = useState(false);
  const [openEditHigherAdmin, setOpenEditHigherAdmin] = useState(false);
  const [request, setRequest] = useState<HigherAdminFeedbackRequest>();
  const [activeSidebar, setActiveSidebar] = useState("respondBack");
  const [activeMainPage, setActiveMainPage] = useState<
    "higherAdmin" | "appeal" | "case"
  >("higherAdmin");

  const fetchData = () => {
    if (!id) return;

    setLoading(true);
    fetchHigherAdminRequestById(id)
      .then((data) => {
        setRequest(data);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_case_details"),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const updateHigherAdminStatus = (
    status: keyof typeof HigherAdminFeedbackRequestStatus,
    coordinatorStatus = "",
  ) => {
    editHigherAdminFeedbackRequest2({
      id: request?.id,
      status: status,
      coordinator_status: coordinatorStatus
        ? coordinatorStatus
        : request?.coordinator_status,
    })
      .then(() => {
        showTooltip(t("successful"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  const appeal = request?.appeal_request as AppealRequest;

  // Header Component
  const renderHeader = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <span className="text-2xl">👨‍⚖️</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("higher_admin_feedback")}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              {request?.status && (
                <span
                  className={`${getStatusChipColor(
                    HigherAdminFeedbackRequestStatus[request?.status],
                  )}`}
                >
                  {HigherAdminFeedbackRequestStatus[request?.status]}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <ActionButton
            onClick={() =>
              setActiveMainPage(
                activeMainPage === "appeal" ? "higherAdmin" : "appeal",
              )
            }
            variant={activeMainPage === "higherAdmin" ? "primary" : "outline"}
          >
            {activeMainPage === "appeal"
              ? `👨‍⚖️ ${t("higher_admin")}`
              : `📝 ${t("appeal")}`}
          </ActionButton>

          {appeal?.case_id && (
            <ActionButton
              onClick={() =>
                setActiveMainPage(
                  activeMainPage === "higherAdmin" ? "case" : "higherAdmin",
                )
              }
              variant={activeMainPage === "case" ? "primary" : "outline"}
            >
              📁 {t("case")}
            </ActionButton>
          )}
        </div>
      </div>
    </div>
  );

  // Higher Admin Request Content
  const renderHigherAdminContent = () => (
    <div className="space-y-4">
      {/* Request Information */}
      <InfoCard title={t("request_details")} defaultOpen={true}>
        <div className="space-y-1">
          {[
            { label: t("requested_by"), value: request?.requested_by ?? "-" },
            {
              label: t("assigned_prosecutor"),
              value: request?.assigned_prosecutor ?? "-",
            },
            {
              label: t("registrar_number"),
              value: request?.registrar_number ?? "-",
            },
            {
              label: t("registrar_date"),
              value: request?.registrar_date
                ? new Date(request.registrar_date).toLocaleDateString()
                : "-",
            },
            { label: t("crime_type"), value: request?.crime_type ?? "-" },
          ].map(
            (item, index) =>
              item.value !== "-" && (
                <InfoRow key={index} label={item.label} value={item.value} />
              ),
          )}
        </div>
      </InfoCard>

      {/* Summary & Attachments */}
      <InfoCard title={t("summary_attachments")}>
        <div className="space-y-4">
          {request?.request_summary && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                {t("request_summary")}
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                {request.request_summary}
              </p>
            </div>
          )}

          <div className="text-sm text-gray-600">
            {t("requested_at")}: {DateView(request?.created_at)}
          </div>

          <div className="space-y-3">
            <DocumentViewerModal
              documents={
                (request?.documents?.filter(
                  (a) => a?.document_type !== "higher_admin_feedback_file",
                ) || []) as any
              }
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.HIGHER_ADMIN}`}
              layout="list"
              hideEmptyFile={true}
            />
          </div>
        </div>
      </InfoCard>

      {/* Higher Admin Feedback */}
      {request?.higher_admin_feedback && (
        <InfoCard title={t("higher_admin_feedback")}>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                {t("feedback")}
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                {request.higher_admin_feedback}
              </p>
            </div>

            <div className="text-sm text-gray-600">
              {t("date")}: {DateView(request?.higher_admin_feedback_date)}
            </div>

            <div className="space-y-3">
              <DocumentViewerModal
                documents={
                  (request?.documents?.filter(
                    (a) => a?.document_type === "higher_admin_feedback_file",
                  ) || []) as any
                }
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.HIGHER_ADMIN}`}
                layout="list"
                hideEmptyFile={true}
              />
            </div>
          </div>
        </InfoCard>
      )}
    </div>
  );

  // Appeal Content (when switching to appeal view)
  const renderAppealContent = () => (
    <div className="space-y-4">
      {/* Basic Appeal Information */}
      <InfoCard title={t("appeal_request")} defaultOpen={true}>
        <div className="space-y-1">
          {[
            {
              label: t("incident_registration_number"),
              value: appeal?.incident_registration_number ?? "-",
            },
            {
              label: t("prosecutor_registration_number"),
              value: appeal?.prosecutor_registration_number ?? "-",
            },
            {
              label: t("crime_reference_number"),
              value: appeal?.crime_registration_number ?? "-",
            },
            {
              label: t("court_registration_number"),
              value: appeal?.court_registration_number ?? "-",
            },
            {
              label: t("appeal_requested_date"),
              value: appeal?.created_at
                ? new Date(appeal.created_at).toLocaleDateString()
                : "-",
            },
            {
              label: t("appeal_type"),
              value: appeal?.appeal_type
                ? AppealRequestType[appeal.appeal_type]
                : "-",
            },
            { label: t("crime_type"), value: appeal?.crime_type || "-" },
            {
              label: t("defendants_name"),
              value: appeal?.defendent_name_if_bail_right || "-",
            },
          ].map(
            (item, index) =>
              item.value !== "-" && (
                <InfoRow key={index} label={item.label} value={item.value} />
              ),
          )}
        </div>
      </InfoCard>

      {/* Lower Court Information */}
      <InfoCard title={t("lower_court_information")}>
        {appeal?.lower_court_type ? (
          <div className="space-y-1">
            {[
              { label: t("lower_court_type"), value: appeal?.lower_court_type },
              {
                label: t("lower_court_level"),
                value: appeal?.lower_court_level,
              },
              {
                label: t("lower_court_hearing_date"),
                value: appeal?.lower_court_hearing_date
                  ? DateView(appeal?.lower_court_hearing_date)
                  : "-",
              },
              {
                label: t("lower_registration_date"),
                value: appeal?.lower_registration_date
                  ? DateView(appeal?.lower_registration_date)
                  : "-",
              },
              {
                label: t("lower_registration_number"),
                value: appeal?.lower_registration_number,
              },
            ].map(
              (item, index) =>
                item.value &&
                item.value !== "-" && (
                  <InfoRow key={index} label={item.label} value={item.value} />
                ),
            )}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            {t("no_lower_court_information")}
          </div>
        )}
      </InfoCard>

      {/* Higher Court Information */}
      <InfoCard title={t("higher_court_information")}>
        {appeal?.higher_court_type ? (
          <div className="space-y-1">
            {[
              {
                label: t("higher_court_type"),
                value: appeal?.higher_court_type,
              },
              {
                label: t("higher_court_level"),
                value: appeal?.higher_court_level,
              },
              {
                label: t("higher_court_hearing_date"),
                value: appeal?.higher_court_hearing_date
                  ? DateView(appeal?.higher_court_hearing_date)
                  : "-",
              },
              {
                label: t("registration_number"),
                value: appeal?.registration_number,
              },
              {
                label: t("registration_date"),
                value: appeal?.registration_date
                  ? DateView(appeal?.registration_date)
                  : "-",
              },
              {
                label: t("court_appeal_number"),
                value: appeal?.court_appeal_number,
              },
              {
                label: t("opening_date"),
                value: appeal ? DateView(appeal?.opening_date) : "-",
              },
              {
                label: t("first_hearing_date"),
                value: appeal?.first_hearidng_date
                  ? DateView(appeal?.first_hearidng_date)
                  : "-",
              },
            ].map(
              (item, index) =>
                item.value &&
                item.value !== "-" && (
                  <InfoRow key={index} label={item.label} value={item.value} />
                ),
            )}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            {t("no_higher_court_information")}
          </div>
        )}
      </InfoCard>

      {/* Defendants */}
      {appeal?.defendants && appeal.defendants.length > 0 && (
        <InfoCard title={t("defendants")} badgeCount={appeal.defendants.length}>
          <div className="space-y-3">
            {appeal.defendants.map((defendant: any, index: number) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {defendant.defendant_info?.first_name}{" "}
                    {defendant.defendant_info?.middle_name}{" "}
                    {defendant.defendant_info?.last_name}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {[
                      defendant.defendant_info?.gender,
                      defendant.defendant_info?.phone_number,
                      defendant.defendant_info?.statement,
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </InfoCard>
      )}

      {/* Summary & Attachments */}
      <InfoCard title={t("summary_attachments")}>
        <div className="space-y-4">
          {appeal?.appeal_summary_text && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                {t("appeal_complain_summary")}
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                {appeal.appeal_summary_text}
              </p>
            </div>
          )}

          {appeal?.reason && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">{t("reason")}</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                {appeal.reason}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <DocumentViewerModal
              documents={(appeal?.documents || []) as any}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.HIGHER_ADMIN}`}
              layout="list"
              hideEmptyFile={true}
            />
          </div>
        </div>
      </InfoCard>
    </div>
  );

  // Action Dropdown for Higher Admin
  const renderHigherAdminActions = () => (
    <div className="space-y-1">
      {isProsecutor &&
        (request?.status === "PENDING" ||
          request?.status === "COORDINATOR_RESPOND_BACK") && (
          <ActionDropdownItem
            onClick={() => {
              setActionDropdownOpen(false);
              updateHigherAdminStatus("FORWARDED_TO_COORDINATOR", "PENDING");
            }}
          >
            📤 {t("forward_to_coordinator")}
          </ActionDropdownItem>
        )}
      {isProsecutor &&
        (request?.status === "PENDING" ||
          request?.status === "COORDINATOR_RESPOND_BACK") && (
          <ActionDropdownItem
            onClick={() => {
              setActionDropdownOpen(false);
              setOpenEditHigherAdmin(true);
            }}
          >
            ✏️ {t("edit_request")}
          </ActionDropdownItem>
        )}
      {request?.status === "FORWARDED_TO_COORDINATOR" &&
        request?.higher_coordinator_status === "PENDING" &&
        isCoordinator && (
          <ActionDropdownItem
            onClick={() => {
              setActionDropdownOpen(false);
              updateHigherAdminStatus("COORDINATOR_APPROVED", "PENDING");
            }}
          >
            ✅ {t("approve")}
          </ActionDropdownItem>
        )}
      {request?.status === "FORWARDED_TO_COORDINATOR" &&
        request?.higher_coordinator_status === "PENDING" &&
        isCoordinator && (
          <ActionDropdownItem
            onClick={() => {
              setActionDropdownOpen(false);
              setOpenRespondBack(true);
            }}
          >
            ↩️ {t("respond_back_to_prosecutor")}
          </ActionDropdownItem>
        )}
      {request?.status === "COORDINATOR_APPROVED" && isCoordinator && (
        <ActionDropdownItem
          onClick={() => {
            setActionDropdownOpen(false);
            updateHigherAdminStatus("FORWARDED_TO_REGISTRAR", "PENDING");
          }}
        >
          📤 {t("forward_to_registrar")}
        </ActionDropdownItem>
      )}
      {request?.status === "FORWARDED_TO_REGISTRAR" &&
        !request?.registrar_number &&
        isRegistrar && (
          <ActionDropdownItem
            onClick={() => {
              setActionDropdownOpen(false);
              setOpenSubmitToHigherAdmin(true);
            }}
          >
            📋 {t("forward_to_higher_admin_registrar")}
          </ActionDropdownItem>
        )}
      {request?.status === "SUBMITTED_TO_HIGHER_ADMIN" &&
        request?.registrar_number &&
        isRegistrar && (
          <ActionDropdownItem
            onClick={() => {
              setActionDropdownOpen(false);
              updateHigherAdminStatus("FORWARDED_TO_HIGHER_COORDINATOR");
            }}
          >
            📤 {t("forward_to_coordinator")}
          </ActionDropdownItem>
        )}
      {request?.status === "FORWARDED_TO_HIGHER_COORDINATOR" &&
        !request?.assigned_prosecutor &&
        isCoordinator && (
          <ActionDropdownItem
            onClick={() => {
              setActionDropdownOpen(false);
              setOpenAssignProsecutor(true);
            }}
          >
            👨‍💼 {t("assign_prosecutor")}
          </ActionDropdownItem>
        )}
      {(request?.status === "ASSIGNED_TO_PROSECUTOR" ||
        request?.status === "RESPONDED_BACK_TO_PROSECUTOR") &&
        request?.higher_coordinator_status !== "APPROVED" &&
        isProsecutor && (
          <ActionDropdownItem
            onClick={() => {
              setActionDropdownOpen(false);
              setOpenHigherAdminFeedback(true);
            }}
          >
            💬 {t("higher_admin_feedback")}
          </ActionDropdownItem>
        )}
      {request?.status === "FORWARDED_TO_HIGHER_COORDINATOR" &&
        request?.higher_admin_feedback &&
        isCoordinator && (
          <ActionDropdownItem
            onClick={async () => {
              setActionDropdownOpen(false);
              editHigherAdminFeedbackRequest2({
                id: request?.id,
                status: "ASSIGNED_TO_PROSECUTOR",
                higher_coordinator_status: "APPROVED",
              })
                .then(() => {
                  showTooltip(t("successful"), "success");
                  fetchData();
                })
                .catch((error) => {
                  showTooltip(
                    error?.response?.data?.detail || errorHandlerOnForm(error),
                    "error",
                  );
                });
            }}
          >
            ✅ {t("approve")}
          </ActionDropdownItem>
        )}
      {request?.status === "FORWARDED_TO_HIGHER_COORDINATOR" &&
        request?.higher_admin_feedback &&
        isCoordinator && (
          <ActionDropdownItem
            onClick={() => {
              setActionDropdownOpen(false);
              setOpenRespondBack(true);
            }}
          >
            ↩️ {t("respond_back_to_prosecutor")}
          </ActionDropdownItem>
        )}
      {request?.higher_coordinator_status === "APPROVED" &&
        request?.status !== "COMPLETED" &&
        isProsecutor && (
          <ActionDropdownItem
            onClick={async () => {
              setActionDropdownOpen(false);
              editHigherAdminFeedbackRequest2({
                id: request?.id,
                status: "COMPLETED",
                admin_unit_id: request?.previous_admin_unit_id,
                org_unit_id: request?.previous_org_unit_id,
              })
                .then(() => {
                  showTooltip(t("successful"), "success");
                  navigate("/higher-admin");
                })
                .catch((error) => {
                  showTooltip(
                    error?.response?.data?.detail || errorHandlerOnForm(error),
                    "error",
                  );
                });
            }}
          >
            ✅ {t("complete")}
          </ActionDropdownItem>
        )}
    </div>
  );

  if (loading) {
    return <Loading label={t("loading_higher_admin_request")} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className=" px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {renderHeader()}

        {activeMainPage === "case" ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <InstituteOfChargeDetail caseId={appeal?.case_id + ""} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Content */}
            <div className="space-y-6">
              {activeMainPage === "higherAdmin" && renderHigherAdminContent()}
              {activeMainPage === "appeal" && renderAppealContent()}
            </div>

            {/* Right Column - Actions & Feedback */}
            <div className="space-y-6">
              {/* Action Section */}
              {activeMainPage === "higherAdmin" && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t("case_actions")}
                  </h3>
                  <div className="relative">
                    <button
                      onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
                      className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      {t("take_action")}
                      <svg
                        className={`w-4 h-4 transition-transform ${
                          actionDropdownOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {actionDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden max-h-96 overflow-y-auto">
                        <div className="p-2">{renderHigherAdminActions()}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Feedback Section */}
              {activeMainPage === "higherAdmin" && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center gap-4">
                      <select
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                        value={activeSidebar}
                        onChange={(e) => setActiveSidebar(e.target.value)}
                      >
                        <option value="respondBack">
                          ↩️ {t("respond_back_feedback")}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="p-6">
                    {request && (
                      <HigherAdminRespondbackFeedback
                        requestId={request?.id ?? ""}
                        fetchData={fetchData}
                        respondback_feedbacks={
                          request?.respondback_feedbacks || []
                        }
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modals */}
        {request && openEditHigherAdmin && (
          <EditHigherAdminFeedback
            higherAdminFeedbackRequest={request}
            onClose={(refetch) => {
              setOpenEditHigherAdmin(false);
              if (refetch) {
                fetchData();
              }
            }}
          />
        )}

        {request?.id && openSubmitToHigherAdmin && (
          <RegistrarSubmittedToHigherAdmin
            feedbackId={request?.id}
            higherAdmin={request}
            onClose={(refetch) => {
              setOpenSubmitToHigherAdmin(false);
              if (refetch) {
                fetchData();
              }
            }}
          />
        )}

        {request?.id && openAssignProsecutor && (
          <HigherAdminAssignProsecutor
            feedbackId={request?.id}
            onClose={(refetch) => {
              setOpenAssignProsecutor(false);
              if (refetch) {
                fetchData();
              }
            }}
          />
        )}

        {openRespondBack && (
          <HigherAdminRespondBackComment
            higherAdminFeedbackId={request?.id}
            respondBackTo={
              request?.status === "FORWARDED_TO_HIGHER_COORDINATOR"
                ? "RESPONDED_BACK_TO_PROSECUTOR"
                : "COORDINATOR_RESPOND_BACK"
            }
            onClose={(refetch) => {
              setOpenRespondBack(false);
              if (refetch) {
                fetchData();
              }
            }}
            type={"defendant"}
          />
        )}

        {request?.id && openHigherAdminFeedback && (
          <RegistrarHigherAdminFeedback
            feedbackId={request?.id}
            feedback={request}
            onClose={(refetch) => {
              setOpenHigherAdminFeedback(false);
              if (refetch) {
                fetchData();
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default HigherAdminDetail;
